import { SupportedChainId, utils } from '@orionprotocol/sdk';
import { createSelector } from '@reduxjs/toolkit';
import { i18n } from '@lingui/core';

import { RootStateType } from '../store';

const { isValidChainId } = utils;

type ValueOf<T> = T[keyof T];

export const getAvailableNetworks = createSelector(
  ({ app }: RootStateType) => {
    const { envs, env } = app;
    if (env === undefined) return {};
    const envInfo = envs[env];
    if (!envInfo) return {};
    return envInfo.networks;
  },
  ({ app }: RootStateType) => app.chains,
  (networks, chains) => {
    if (networks) {
      type ChainInfo = ValueOf<typeof chains>;
      return Object.entries(networks).reduce<
        Partial<Record<SupportedChainId, ChainInfo>>
      >((prev, [chId, { rpc }]) => {
        if (!isValidChainId(chId)) {
          throw new Error(i18n._('Invalid chain ID {chId}', { chId }));
        }
        const chain = chains[chId];
        if (!chain) {
          throw new Error(i18n._('No chain with chain id {chId}', { chId }));
        }
        return {
          ...prev,
          [chId]: {
            ...chain,
            ...(rpc && {
              rpc,
            }),
          },
        };
      }, {});
    }
    return {};
  },
);
export const getEnvironment = (state: RootStateType) => state.app.env;
export const getEnvironments = (state: RootStateType) => state.app.envs;
export const getChains = (state: RootStateType) => state.app.chains;
export const getAppChainId = (state: RootStateType) => state.app.chainId;
export const getPriceIntervalUpdated = (state: RootStateType) =>
  state.app.priceIntervalUpdated;
export const getNativeCurrencyWrappedTokenAddress = (state: RootStateType) =>
  state.app.nativeCurrencyWrappedTokenAddress;
export const getIsCustomConfig = (state: RootStateType) =>
  state.app.isCustomConfig;
export const getIsShownChangeNetworkPopup = (state: RootStateType) =>
  state.app.isShownChangeNetworkPopup;
export const getForceEnableAllFeatures = ({ app }: RootStateType) =>
  app.forceEnableAllFeatures;
export const getMobileMenuIsOpen = ({ app }: RootStateType) =>
  app.mobileMenuIsOpen;
export const getAppNetwork = createSelector(
  getAppChainId,
  getChains,
  (appChainId, chains) => chains[appChainId],
);
export const getNativeCurrencySymbol = createSelector(
  getAppNetwork,
  (appNetwork) => appNetwork?.baseCurrencyName,
);
